import { useEffect, useState } from "react";
import { Plus, Users, ShieldCheck, User as UserIcon, KeyRound, UserX, UserCheck, Copy, Trash2, Pencil } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Navigate } from "react-router-dom";

const EMPTY = { email: "", name: "", password: "", role_id: "", manager_id: "" };

function randomPassword(len = 10) {
  const chars = "abcdefghjkmnpqrstuvwxyzABCDEFGHJKMNPQRSTUVWXYZ23456789";
  let out = "";
  for (let i = 0; i < len; i++) out += chars[Math.floor(Math.random() * chars.length)];
  return out;
}

export default function Team() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ ...EMPTY, password: randomPassword() });
  const [invite, setInvite] = useState(null); // freshly created creds to show once
  const [resetFor, setResetFor] = useState(null);
  const [newPwd, setNewPwd] = useState("");

  const load = async () => {
    try {
      const [u, r] = await Promise.all([api.get("/users"), api.get("/roles")]);
      setUsers(u.data);
      setRoles(r.data);
      // Default new-user role to Sales Rep if present
      const sales = r.data.find((x) => x.name === "Sales Rep") || r.data.find((x) => !x.is_system) || r.data[0];
      setForm((f) => ({ ...f, role_id: f.role_id || (sales?.id || "") }));
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { if (me && me.role === "admin") load(); }, [me]);

  if (me && me.role !== "admin") return <Navigate to="/dashboard" replace />;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email.trim() || !form.name.trim() || !form.password.trim() || !form.role_id) { toast.error("Email, name, password and role are required"); return; }
    try {
      const res = await api.post("/users", form);
      setInvite({ email: res.data.email, password: form.password, name: res.data.name });
      const sales = roles.find((x) => x.name === "Sales Rep");
      setForm({ ...EMPTY, password: randomPassword(), role_id: sales?.id || form.role_id });
      setOpen(false);
      load();
    } catch (e2) { toast.error(formatApiError(e2)); }
  };

  const changeRole = async (u, role_id) => {
    try { await api.patch(`/users/${u.id}`, { role_id }); toast.success("Role updated"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const changeManager = async (u, manager_id) => {
    try { await api.patch(`/users/${u.id}`, { manager_id: manager_id === "__none__" ? "" : manager_id }); toast.success("Manager updated"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  // Build a quick id → user map and a downstream-descendants helper so the
  // manager dropdown never lists a user's own reports (would create a cycle).
  const usersById = Object.fromEntries(users.map((u) => [u.id, u]));
  const descendantsOf = (rootId) => {
    const out = new Set();
    let frontier = new Set([rootId]);
    while (frontier.size) {
      const next = new Set();
      for (const u of users) {
        if (frontier.has(u.manager_id) && !out.has(u.id) && u.id !== rootId) {
          out.add(u.id);
          next.add(u.id);
        }
      }
      frontier = next;
    }
    return out;
  };
  const managerOptionsFor = (u) => {
    const banned = descendantsOf(u.id);
    return users.filter((x) =>
      x.id !== u.id &&
      x.is_active !== false &&
      x.role_name !== "Admin" &&
      !banned.has(x.id)
    );
  };
  const managerOptionsForNew = () =>
    users.filter((x) => x.is_active !== false && x.role_name !== "Admin");

  const renameUser = async (u) => {
    const next = window.prompt(`Display name for ${u.email}`, u.name || "");
    if (next === null) return; // user cancelled
    const trimmed = next.trim();
    if (!trimmed) { toast.error("Name cannot be empty"); return; }
    if (trimmed === u.name) return;
    try { await api.patch(`/users/${u.id}`, { name: trimmed }); toast.success("Name updated"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const toggleActive = async (u) => {
    try { await api.patch(`/users/${u.id}`, { is_active: !(u.is_active !== false) }); toast.success(u.is_active !== false ? "Deactivated" : "Reactivated"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const submitReset = async (e) => {
    e.preventDefault();
    if (!newPwd || newPwd.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    try {
      await api.post(`/users/${resetFor.id}/reset-password`, { new_password: newPwd });
      setInvite({ email: resetFor.email, password: newPwd, name: resetFor.name });
      setResetFor(null); setNewPwd("");
    } catch (e2) { toast.error(formatApiError(e2)); }
  };

  const removeUser = async (u) => {
    if (!window.confirm(`Delete ${u.name}? This cannot be undone.`)) return;
    try { await api.delete(`/users/${u.id}`); toast.success("User deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const copyCreds = async () => {
    if (!invite) return;
    const text = `Login for ${invite.name}\nURL: ${window.location.origin}\nEmail: ${invite.email}\nPassword: ${invite.password}`;
    try { await navigator.clipboard.writeText(text); toast.success("Copied to clipboard"); }
    catch (_e) { toast.error("Copy failed — select the text manually"); }
  };

  return (
    <div data-testid="team-page">
      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">Access</div>
          <h1 className="font-heading text-3xl font-semibold text-slate-900">Team</h1>
          <p className="text-slate-500 text-sm mt-1">Invite sales reps so leads and demos can be assigned to real people. Admins can invite, reset passwords, and deactivate members.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-indigo-600 hover:bg-indigo-700" data-testid="new-user-btn"><Plus className="w-4 h-4 mr-1.5" /> Invite member</Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader><DialogTitle>Invite team member</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-3">
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Full name *</Label>
                <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-name-input" className="mt-1" />
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Email *</Label>
                <Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-email-input" className="mt-1" />
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Temporary password *</Label>
                <div className="flex gap-2 mt-1">
                  <Input required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="user-password-input" />
                  <Button type="button" variant="outline" onClick={() => setForm({ ...form, password: randomPassword() })} data-testid="regen-password-btn">Regenerate</Button>
                </div>
                <p className="text-xs text-slate-500 mt-1">Share this password with the user out-of-band (chat, SMS). They can change it later.</p>
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Role</Label>
                <Select value={form.role_id} onValueChange={(v) => setForm({ ...form, role_id: v })}>
                  <SelectTrigger className="mt-1" data-testid="user-role-select"><SelectValue placeholder="Pick a role" /></SelectTrigger>
                  <SelectContent>
                    {roles.map((r) => (
                      <SelectItem key={r.id} value={r.id}>{r.name}{r.is_system ? " · system" : ""}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {(() => {
                const roleName = roles.find((r) => r.id === form.role_id)?.name;
                if (roleName === "Admin") return null;
                const managers = managerOptionsForNew();
                return (
                  <div>
                    <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Reports to (Manager)</Label>
                    <Select value={form.manager_id || "__none__"} onValueChange={(v) => setForm({ ...form, manager_id: v === "__none__" ? "" : v })}>
                      <SelectTrigger className="mt-1" data-testid="user-manager-select"><SelectValue placeholder="No manager" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">No manager</SelectItem>
                        {managers.map((m) => (
                          <SelectItem key={m.id} value={m.id}>
                            {m.name}{m.role_name ? ` · ${m.role_name}` : ""}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-slate-500 mt-1">Managers see their reports' leads, clients and quotations.</p>
                  </div>
                );
              })()}
              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700" data-testid="user-save-btn">Create</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {invite && (
        <div className="mb-4 border border-emerald-300 bg-emerald-50 rounded-md p-4" data-testid="invite-credentials">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="font-semibold text-emerald-900 text-sm">Share these credentials with <b>{invite.name}</b></div>
              <div className="mt-2 text-sm font-mono bg-white border border-emerald-200 rounded px-3 py-2 text-slate-800">
                <div>Email: <span data-testid="invite-email">{invite.email}</span></div>
                <div>Password: <span data-testid="invite-password">{invite.password}</span></div>
              </div>
              <div className="text-xs text-emerald-800 mt-2">This password is shown once. Copy it now.</div>
            </div>
            <div className="flex flex-col gap-2">
              <Button size="sm" variant="outline" onClick={copyCreds} data-testid="copy-creds-btn"><Copy className="w-3.5 h-3.5 mr-1" /> Copy</Button>
              <Button size="sm" variant="ghost" onClick={() => setInvite(null)} data-testid="dismiss-invite-btn">Dismiss</Button>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Name</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Email</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Role</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Manager</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Status</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {users.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="text-center text-slate-400 py-10"><Users className="w-8 h-8 mx-auto mb-2 text-slate-300" /> No team members yet.</TableCell></TableRow>
            ) : users.map((u) => {
              const isSelf = me?.id === u.id;
              const active = u.is_active !== false;
              return (
                <TableRow key={u.id} className="hover:bg-slate-50" data-testid={`user-row-${u.id}`}>
                  <TableCell className="font-medium text-slate-900 text-sm">
                    <div className="flex items-center gap-2 group">
                      {u.role_name === "Admin" ? <ShieldCheck className="w-4 h-4 text-indigo-600" /> : <UserIcon className="w-4 h-4 text-slate-400" />}
                      <span>{u.name} {isSelf && <span className="text-xs text-slate-500">(you)</span>}</span>
                      <button type="button" onClick={() => renameUser(u)} className="opacity-0 group-hover:opacity-100 transition text-slate-400 hover:text-slate-700" title="Rename" data-testid={`rename-btn-${u.id}`}>
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </TableCell>
                  <TableCell className="text-sm text-slate-600">{u.email}</TableCell>
                  <TableCell>
                    <Select value={u.role_id || ""} onValueChange={(v) => changeRole(u, v)} disabled={isSelf}>
                      <SelectTrigger className="w-40 h-8" data-testid={`role-select-${u.id}`}><SelectValue placeholder="—" /></SelectTrigger>
                      <SelectContent>
                        {roles.map((r) => (
                          <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell>
                    {u.role_name === "Admin" ? (
                      <span className="text-xs text-slate-400">—</span>
                    ) : (
                      <Select value={u.manager_id || "__none__"} onValueChange={(v) => changeManager(u, v)}>
                        <SelectTrigger className="w-52 h-8" data-testid={`manager-select-${u.id}`}>
                          <SelectValue placeholder="— No manager">
                            {u.manager_id && usersById[u.manager_id]
                              ? usersById[u.manager_id].name
                              : (u.manager_id ? "Unknown manager" : "— No manager")}
                          </SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">— No manager</SelectItem>
                          {managerOptionsFor(u).map((m) => (
                            <SelectItem key={m.id} value={m.id}>
                              {m.name}{m.role_name ? ` · ${m.role_name}` : ""}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className={active ? "border-emerald-300 text-emerald-700 bg-emerald-50" : "border-slate-300 text-slate-500 bg-slate-50"}>
                      {active ? "Active" : "Deactivated"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button size="sm" variant="outline" onClick={() => { setResetFor(u); setNewPwd(randomPassword()); }} data-testid={`reset-btn-${u.id}`}>
                        <KeyRound className="w-3.5 h-3.5 mr-1" /> Reset password
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => toggleActive(u)} disabled={isSelf} data-testid={`toggle-active-btn-${u.id}`}>
                        {active ? (<><UserX className="w-3.5 h-3.5 mr-1" /> Deactivate</>) : (<><UserCheck className="w-3.5 h-3.5 mr-1" /> Reactivate</>)}
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => removeUser(u)} disabled={isSelf} className="text-red-600 hover:text-red-700 border-red-200" data-testid={`delete-btn-${u.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!resetFor} onOpenChange={(v) => !v && setResetFor(null)}>
        <DialogContent className="max-w-sm" data-testid="reset-password-dialog">
          <DialogHeader><DialogTitle>Reset password — {resetFor?.name}</DialogTitle></DialogHeader>
          <form onSubmit={submitReset} className="space-y-3">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">New password</Label>
              <div className="flex gap-2 mt-1">
                <Input required value={newPwd} onChange={(e) => setNewPwd(e.target.value)} data-testid="reset-password-input" />
                <Button type="button" variant="outline" onClick={() => setNewPwd(randomPassword())}>Regenerate</Button>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setResetFor(null)}>Cancel</Button>
              <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700" data-testid="reset-password-submit-btn">Set new password</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
